import React, { useState } from 'react'
import ButtonIcon from '@/components/ui/ButtonIcon'
import Button from '@/components/ui/button'
import { MagnifyIcon, CrossIcon } from '@/assets/icons'
import './InputText.scss'

/**
 * InputText — single-line text input with an optional trailing action.
 *
 * Visual base derived from the navbar search input (SearchBox), promoted to a
 * generic, reusable component.
 *
 * Trailing variants:
 *  - "icon"   → ButtonIcon (default: MagnifyIcon)
 *  - "button" → Button with label
 *  - "none"   → no trailing element
 */
const InputText = ({
  value: controlledValue,
  defaultValue = '',
  placeholder = '',
  id,
  className = '',
  disabled = false,
  showClear = true,
  variant = 'none', // 'icon' | 'button' | 'none'
  icon = null,
  buttonLabel = 'Search',
  /** Spinner on the trailing "button" variant while its action is in flight. */
  buttonLoading = false,
  error = '',
  onChange = () => {},
  onEnter = () => {},
  /** Trailing action handler. Defaults to `onEnter` so the Enter key and the trailing
   *  control stay in sync; pass it explicitly when the action is not "submit the value". */
  onAction,
  onClear = () => {},
  onFocus = () => {},
  onBlur = () => {},
  buttonTestId,
  /** Disables the trailing control alone — the field stays writable. */
  buttonDisabled = false,
  autoFocus = false,
}) => {
  const isControlled = controlledValue !== undefined
  const [internalValue, setInternalValue] = useState(defaultValue)
  const value = isControlled ? controlledValue : internalValue

  const updateValue = next => {
    if (!isControlled) setInternalValue(next)
    onChange(next)
  }

  const handleChange = e => updateValue(e.target.value)

  const handleKeyDown = e => {
    if (e.key === 'Enter') onEnter(value)
  }

  const handleClear = () => {
    updateValue('')
    onClear()
  }

  const handleAction = () => (onAction ? onAction(value) : onEnter(value))

  const renderTrailing = () => {
    if (variant === 'icon') {
      return (
        <>
          <span className="input-text__divider" aria-hidden />
          <ButtonIcon
            variant="normal"
            size="small"
            cursorPointer
            highlightOnHover
            isDisabled={disabled}
            handleOnClick={handleAction}
            ariaLabel={buttonLabel}>
            {icon || <MagnifyIcon />}
          </ButtonIcon>
        </>
      )
    }
    if (variant === 'button') {
      return (
        <Button
          onClick={handleAction}
          disabled={disabled || buttonDisabled}
          loading={buttonLoading}
          // 32px inside the 48px field: 8px of clearance top and bottom, matching
          // the vertical inset of the icon variant's divider
          size="small"
          type="text-only"
          buttonType="button"
          data-testid={buttonTestId}>
          {buttonLabel}
        </Button>
      )
    }
    return null
  }

  return (
    <div className="input-text-wrapper">
      <div
        className={`input-text input-text--${variant} ${
          disabled ? 'input-text--disabled' : ''
        } ${error ? 'input-text--error' : ''} ${className}`}>
        <input
          type="text"
          className="input-text__input"
          placeholder={placeholder}
          value={value}
          id={id}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          autoFocus={autoFocus}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {showClear && value && !disabled && (
          <button
            type="button"
            className="input-text__clear"
            onClick={handleClear}
            aria-label="Clear">
            <CrossIcon width="12px" height="12px" />
          </button>
        )}
        {renderTrailing()}
      </div>
      {error && <span className="input-text__error">{error}</span>}
    </div>
  )
}

export default InputText
